// covered-by: frontends/carrie/src/ask-v2/transport/__tests__/createAskV2Transport.test.ts
import {
  ASK_PUNTT_QUOTED_CONTEXT_MAX_CHARS,
  ASK_PUNTT_QUOTED_CONTEXT_MAX_COUNT,
  type AskPunttQuotedContext,
} from "@mg/schemas/src/christo/askPuntt";

export type AskV2ThreadRole = "user" | "assistant";

export type AskV2ThreadStatus =
  | "sending"
  | "sent"
  | "failed"
  | "streaming"
  | "aborted";

export type AskV2Attachment = {
  key: string;
  source: string;
  type: "image" | "video" | "file";
  name?: string;
};

// why: single wire shape — re-export the zod-inferred schema type under the
// FE-preferred name instead of hand-mirroring it, so a schema field change is a
// compile error here rather than a silent JSON.stringify divergence.
export type AskV2QuotedContext = AskPunttQuotedContext;

// FE-local, but sourced from the shared bounds so the composer trim/cap and the
// server `.max()` cannot drift.
export const ASK_V2_QUOTED_CONTEXT_MAX_CHARS =
  ASK_PUNTT_QUOTED_CONTEXT_MAX_CHARS;
export const ASK_V2_QUOTED_CONTEXT_MAX_COUNT =
  ASK_PUNTT_QUOTED_CONTEXT_MAX_COUNT;

export type AskV2SendOpts = {
  /** Send this text without surfacing it in the composer (form / atom answers). */
  message?: string;
  attachments?: AskV2Attachment[];
  mentions?: string[];
  mentionEmails?: string[];
};

export type AskV2ThreadMessage = {
  clientId: string;
  serverId?: string;
  role: AskV2ThreadRole;
  content: string;
  status: AskV2ThreadStatus;
  createdAt?: string;
  userId?: string;
  /** Local-only: ready attachments on this turn so retry can resend them. */
  attachments?: AskV2Attachment[];
  quotedContexts?: AskV2QuotedContext[];
  /**
   * Local-only: the @mentions this turn asked for. christo reads the access
   * grant target from `body.mentions`, not the text, so a retried or edited
   * turn that drops these silently skips the grant.
   */
  mentions?: string[];
  mentionEmails?: string[];
};

export type AskV2TransportSnapshot = {
  chatId: string | null;
  composer: string;
  quotedContexts: AskV2QuotedContext[];
  messages: AskV2ThreadMessage[];
  inFlight: boolean;
  sending: boolean;
  aborting: boolean;
  editingClientId: string | null;
  editDraft: string;
};

export type AskV2ChatAck = {
  chatId?: string;
  userMessage: { id: string; content: string };
  /**
   * Live create/send often omit this (workflow still running). Missing or
   * empty content means the turn is in-flight. Present content is a finished
   * greeting. Absence does not mean "no turn started."
   */
  assistantMessage?: { id: string; content: string };
};

export type AskV2ChatApi = {
  createChat: (input: {
    message: string;
    attachments?: AskV2Attachment[];
    quotedContexts?: AskV2QuotedContext[];
    mentions?: string[];
    mentionEmails?: string[];
  }) => Promise<AskV2ChatAck>;
  sendMessage: (input: {
    chatId: string;
    message: string;
    attachments?: AskV2Attachment[];
    quotedContexts?: AskV2QuotedContext[];
    mentions?: string[];
    mentionEmails?: string[];
  }) => Promise<AskV2ChatAck>;
  abortMessage: (input: { chatId: string; messageId: string }) => Promise<void>;
  regenerateMessage: (input: {
    chatId: string;
    messageId: string;
    content: string;
  }) => Promise<AskV2ChatAck>;
};
